/**
 * =============================================================================
 * DASHBOARD REALTIME INTEGRATION
 * =============================================================================
 * 
 * PHASE 2C: Real-Time Operating System
 * 
 * Connects the Operational Intelligence dashboard to WebSocket events for:
 * - Live KPI updates
 * - Real-time alert notifications
 * - Activity feed streaming
 * - Connection status indicator
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { Wifi, WifiOff, RefreshCw, Bell, AlertTriangle, CheckCircle, Briefcase, Users, Clock } from 'lucide-react';
import { EVENT_TYPES, eventToToast } from '@/hooks/useRealtime';

// =============================================================================
// CONNECTION STATUS INDICATOR
// =============================================================================
export function ConnectionStatus({ isConnected, lastEvent, onReconnect }) {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div 
      className="relative"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <button
        onClick={onReconnect}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition ${
          isConnected 
            ? 'bg-green-50 text-green-700 hover:bg-green-100' 
            : 'bg-red-50 text-red-700 hover:bg-red-100'
        }`}
        data-testid="connection-status"
      >
        {isConnected ? (
          <>
            <div className="relative">
              <Wifi className="w-3.5 h-3.5" />
              <div className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            </div>
            <span>Live</span>
          </>
        ) : (
          <>
            <WifiOff className="w-3.5 h-3.5" />
            <span>Offline</span>
            <RefreshCw className="w-3 h-3 ml-1" />
          </>
        )}
      </button>

      {/* Tooltip */}
      {showTooltip && (
        <div className="absolute top-full left-0 mt-2 w-48 bg-slate-800 text-white text-xs rounded-lg p-3 shadow-lg z-50">
          <p className="font-medium mb-1">
            {isConnected ? 'Connected to live updates' : 'Disconnected from server'}
          </p>
          {lastEvent && (
            <p className="text-slate-300">
              Last event: {new Date(lastEvent.timestamp).toLocaleTimeString()}
            </p>
          )}
          {!isConnected && (
            <p className="text-amber-300 mt-1">Click to reconnect</p>
          )}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// LIVE NOTIFICATION TOAST
// =============================================================================
export function showRealtimeNotification(event) {
  const toastData = eventToToast(event);
  if (!toastData) return;

  const iconMap = {
    [EVENT_TYPES.JOB_CREATED]: Briefcase,
    [EVENT_TYPES.JOB_UPDATED]: RefreshCw,
    [EVENT_TYPES.CONTRACTOR_ASSIGNED]: Users,
    [EVENT_TYPES.SLA_BREACH]: AlertTriangle,
  };

  const Icon = iconMap[event.type] || Bell;

  switch (toastData.type) {
    case 'warning':
      toast.warning(toastData.message, {
        description: toastData.title,
        duration: toastData.duration,
        icon: <Icon className="w-4 h-4" />,
      });
      break;
    case 'success':
      toast.success(toastData.message, {
        description: toastData.title,
        duration: toastData.duration,
        icon: <Icon className="w-4 h-4" />,
      });
      break;
    default:
      toast.info(toastData.message, {
        description: toastData.title,
        duration: toastData.duration,
        icon: <Icon className="w-4 h-4" />,
      });
  }
}

// =============================================================================
// AUTO-REFRESH HOOK
// =============================================================================
export function useAutoRefresh(refreshFn, interval = 60000, enabled = true) {
  const intervalRef = useRef(null);
  const [lastRefresh, setLastRefresh] = useState(Date.now());

  const refresh = useCallback(async () => {
    try {
      await refreshFn();
      setLastRefresh(Date.now());
    } catch (err) {
      console.error('Auto-refresh failed:', err);
    }
  }, [refreshFn]);

  useEffect(() => {
    if (!enabled) return;

    intervalRef.current = setInterval(refresh, interval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [refresh, interval, enabled]);

  return { lastRefresh, triggerRefresh: refresh };
}

// =============================================================================
// SLA BREACH ALERT COMPONENT
// =============================================================================
export function SLABreachAlert({ breach, onDismiss, onViewJob }) {
  if (!breach) return null;

  return (
    <div 
      className="fixed bottom-6 right-6 max-w-sm bg-red-600 text-white rounded-xl shadow-2xl p-4 animate-slide-up z-50"
      data-testid="sla-breach-alert"
    >
      <div className="flex items-start gap-3">
        <div className="p-2 bg-red-500 rounded-lg flex-shrink-0">
          <AlertTriangle className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-bold text-sm">SLA BREACH</h4>
          <p className="text-red-100 text-xs mt-0.5">
            {breach.data?.job_reference || 'Job'} - {breach.data?.breach_type || 'SLA'} overdue
          </p>
          <div className="flex items-center gap-2 mt-3">
            <button
              onClick={() => onViewJob?.(breach.job_id)}
              className="px-3 py-1.5 bg-white text-red-600 text-xs font-medium rounded-lg hover:bg-red-50 transition"
            >
              View Job
            </button>
            <button
              onClick={onDismiss}
              className="px-3 py-1.5 bg-red-500 text-white text-xs font-medium rounded-lg hover:bg-red-400 transition"
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// REALTIME DASHBOARD WRAPPER
// =============================================================================
export function RealtimeDashboardProvider({ 
  children, 
  onJobCreated, 
  onJobUpdated, 
  onSLABreach,
  onRefreshNeeded,
  channel = 'wyldecrest_helpdesk'
}) {
  const [isConnected, setIsConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState(null);
  const [pendingBreach, setPendingBreach] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const reconnectAttempts = useRef(0);

  const connect = useCallback(() => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
    const host = backendUrl.replace(/^https?:\/\//, '');
    const url = `${wsProtocol}://${host}/api/realtime/ws/${channel}?user_id=dashboard`;

    try {
      wsRef.current = new WebSocket(url);

      wsRef.current.onopen = () => {
        setIsConnected(true);
        reconnectAttempts.current = 0;
        
        // Start ping interval
        pingIntervalRef.current = setInterval(() => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send('ping');
          }
        }, 30000);
      };

      wsRef.current.onmessage = (msg) => {
        try {
          if (msg.data === 'pong') return;
          
          const event = JSON.parse(msg.data);
          if (['connected', 'pong', 'status'].includes(event.type)) return;

          setLastEvent(event);

          // Handle different event types
          switch (event.type) {
            case EVENT_TYPES.JOB_CREATED:
              onJobCreated?.(event);
              showRealtimeNotification(event);
              onRefreshNeeded?.();
              break;
              
            case EVENT_TYPES.JOB_UPDATED:
              onJobUpdated?.(event);
              if (event.data?.status_change) {
                showRealtimeNotification(event);
              }
              onRefreshNeeded?.();
              break;
              
            case EVENT_TYPES.CONTRACTOR_ASSIGNED:
              showRealtimeNotification(event);
              onRefreshNeeded?.();
              break;
              
            case EVENT_TYPES.SLA_BREACH:
              setPendingBreach(event);
              onSLABreach?.(event);
              onRefreshNeeded?.();
              break;
              
            default:
              break;
          }
        } catch (err) {
          // Silent parse error
        }
      };

      wsRef.current.onclose = () => {
        setIsConnected(false);
        clearInterval(pingIntervalRef.current);

        // Reconnect with exponential backoff
        if (reconnectAttempts.current < 5) {
          const delay = Math.min(2000 * Math.pow(2, reconnectAttempts.current), 32000);
          reconnectAttempts.current += 1;
          reconnectTimeoutRef.current = setTimeout(connect, delay);
        }
      };

      wsRef.current.onerror = () => {
        // Silent error
      };
    } catch (err) {
      // Silent connection failure
    }
  }, [channel, onJobCreated, onJobUpdated, onSLABreach, onRefreshNeeded]);

  const disconnect = useCallback(() => {
    clearInterval(pingIntervalRef.current);
    clearTimeout(reconnectTimeoutRef.current);
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  useEffect(() => {
    connect();
    return () => disconnect();
  }, [connect, disconnect]);

  const handleDismissBreach = () => setPendingBreach(null);

  return (
    <>
      {React.Children.map(children, child => 
        React.isValidElement(child) 
          ? React.cloneElement(child, { 
              isConnected, 
              lastEvent, 
              onReconnect: connect 
            })
          : child
      )}
      
      {/* SLA Breach Alert Popup */}
      {pendingBreach && (
        <SLABreachAlert
          breach={pendingBreach}
          onDismiss={handleDismissBreach}
          onViewJob={(jobId) => {
            handleDismissBreach();
            window.location.href = `/phs/helpdesk/job/${jobId}`;
          }}
        />
      )}
    </>
  );
}

// =============================================================================
// ACTIVITY PULSE INDICATOR
// =============================================================================
export function ActivityPulse({ events = [], maxEvents = 5 }) {
  const recentEvents = events.slice(0, maxEvents);
  
  if (recentEvents.length === 0) return null;

  return (
    <div className="flex items-center gap-2">
      <div className="relative">
        <Bell className="w-4 h-4 text-teal-600" />
        <div className="absolute -top-1 -right-1 w-3 h-3 bg-teal-500 rounded-full flex items-center justify-center">
          <span className="text-[8px] font-bold text-white">{recentEvents.length}</span>
        </div>
      </div>
      <span className="text-xs text-slate-500">
        {recentEvents.length} new {recentEvents.length === 1 ? 'event' : 'events'}
      </span>
    </div>
  );
}

// =============================================================================
// KPI UPDATE FLASH
// =============================================================================
export function KPIUpdateFlash({ value, previousValue, duration = 2000 }) {
  const [isFlashing, setIsFlashing] = useState(false);

  useEffect(() => {
    if (value !== previousValue && previousValue !== undefined) {
      setIsFlashing(true);
      const timer = setTimeout(() => setIsFlashing(false), duration);
      return () => clearTimeout(timer);
    }
  }, [value, previousValue, duration]);

  return (
    <span className={`transition-all duration-300 ${
      isFlashing ? 'text-teal-600 scale-110' : ''
    }`}>
      {value}
    </span>
  );
}

export default {
  ConnectionStatus,
  showRealtimeNotification,
  useAutoRefresh,
  SLABreachAlert,
  RealtimeDashboardProvider,
  ActivityPulse,
  KPIUpdateFlash,
};
